import ResultType from "./resultType";

/**
 * An enum in charge of holding SortBy information as the SortBy name
 * and the possible query strings values for each SortBy key.
 */
export default class SortType {
  /**
   * Key for city sorting. It could be from A-Z or Z-A.
   */
  static get City() {
    const configuration = {
      queryStringValueDescending: 'city-z-a',
      queryStringValueAscending: 'city-a-z',
      name: 'city',
      validResultTypes: [ResultType.Communities, ResultType.Homes, ResultType.Locations],
    };

    return this.getInstance('city', configuration);
  }

  /**
   * Key for price sorting. It could be from low to high or from high to low.
   */
  static get Price() {
    const configuration = {
      queryStringValueDescending: 'price-high-low',
      queryStringValueAscending: 'price-low-high',
      name: 'price',
      validResultTypes: [ResultType.Communities, ResultType.Homes, ResultType.Locations],
    };

    return this.getInstance('price', configuration);
  }

  /**
   * Key for bed sorting. It could be from low to high or from high to low.
   */
  static get Bed() {
    const configuration = {
      queryStringValueDescending: 'bed-high-low',
      queryStringValueAscending: 'bed-low-high',
      name: 'bed',
      validResultTypes: [ResultType.Homes],
    };

    return this.getInstance('bed', configuration);
  }

  /**
   * Key for builders sorting. It could be from A-Z or Z-A.
   */
  static get Builders() {
    const configuration = {
      queryStringValueDescending: 'builder-z-a',
      queryStringValueAscending: 'builder-a-z',
      name: 'builders',
      validResultTypes: [ResultType.Communities, ResultType.Homes, ResultType.Locations],
    };

    return this.getInstance('builders', configuration);
  }

  /**
   * Key for community name sorting. It could be from A-Z or Z-A.
   */
  static get Community() {
    const configuration = {
      queryStringValueDescending: 'community-z-a',
      queryStringValueAscending: 'community-a-z',
      name: 'community',
      validResultTypes: [ResultType.Communities, ResultType.Locations],
    };

    return this.getInstance('community', configuration);
  }

  /**
   * Key for community id sorting.
   */
  static get CommunityId() {
    const configuration = {
      name: 'communityId',
      validResultTypes: [ResultType.Homes, ResultType.Communities, ResultType.Locations],
    };

    return this.getInstance('communityId', configuration);
  }

  /**
   * The function returns a value from a map based on the input name.
   *
   * @param name - The name of the key in the `statusMap` object that we want to retrieve the value for.
   *
   * @returns the value of the `statusMap` object that corresponds to the `name` parameter passed to the function.
   * If `name` matches one of the keys in `statusMap`, the corresponding value is returned.
   */
  static fromKey(name) {
    const statusMap = {
      city: SortType.City,
      price: SortType.Price,
      bed: SortType.Bed,
      community: SortType.Community,
      builders: SortType.Builders,
      communityId: SortType.CommunityId,
    };

    return statusMap[name];
  }

  /**
   * Gets an instance of a SortType object based on a given name.
   * If the instance does not already exist, it creates a new instance using the configuration provided.
   *
   * @param name a unique identifier used to retrieve or create an instance of the SortType class.
   * @param configuration an object that contains various properties and values that are used to configure
   * the instance of the SortType class that is being created.
   *
   * @returns an instance of `SortType` that corresponds to the given `name`.
   */
  static getInstance(name, configuration) {
    configuration.name = name;

    if (!this.instances[name]) {
      this.instances[name] = new SortType(configuration);
    }

    return this.instances[name];
  }

  constructor(configuration) {
    this.name = configuration.name;
    this.queryStringValueAscending = configuration.queryStringValueAscending;
    this.queryStringValueDescending = configuration.queryStringValueDescending;
    this.validResultTypes = configuration.validResultTypes;
  }

  /**
   * getQueryStringValue: gets the SortType's instance query string value.
   * @param {boolean} isAscendingOrder is a flag indicating if the SortType instance has to retrieve
   * its ascending or descending query string value. 
   * @returns 
   */
  getQueryStringValue(isAscendingOrder) {
    return isAscendingOrder ? this.queryStringValueAscending : this.queryStringValueDescending;
  }

  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(SortType, 'instances', { value: {} });
